import { Box, Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import InAppHome from '@/karehero-app/tell-us-once/intake/InAppHome';
import ProcessingChatScreen from '@/karehero-app/tell-us-once/intake/ProcessingChatScreen';

const noop = () => {};
import { type HandoverSpec } from '@/designs-dev/handover/types';
import Flow from '@/karehero-app/tell-us-once/intake/Flow';
import PlaceholderScreen from '@/karehero-app/tell-us-once/intake/PlaceholderScreen';
import HomeScreen from '@/karehero-app/home/HomeScreen';
import LockScreen, { PushNotification } from '@/designs-dev/device/LockScreen';
import {
  INTAKE_PING,
  INTAKE_PING_PROBLEM,
} from '@/karehero-app/tell-us-once/intake/content';
import { AI_CONSENT_DECLINED } from '@/karehero-app/tell-us-once/intake/content';

// The feature itself, as it is being designed. The thinking and where it went is
// next door under Concepting; this page is for what the build is actually
// getting.
//
// The steps below are the chat flow's, written out here rather than imported
// from the Concepting spec. Concepting is free to be reshuffled, and this page
// should not move when it is.
export const intakeProfileReviewSpec: HandoverSpec = {
  id: 'intake-profile-review',
  title: 'Onboarding 3.0',
  description: '',
  links: [],
  // Tagged as sections so each one can carry its own states: what editing an
  // answer looks like belongs under the profile review, not in one pile at the
  // end of the page with the upload and send failures.
  flows: [
    {
      title: 'Intake',
      journey: 'Intake',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              After account creation, when the user is verified, this flow
              replaces the form onboarding, offering them a more humanistic and
              multimodal introduction to the app.
            </span>
            <span>
              The aim is a basic profile, gathered from several sources. One
              document should give us more than one fact.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'youre-in',
          title: "You're in",
          render: () => <Flow initial='youre-in' option={2} />,
        },
        {
          id: 'ai-consent',
          title: 'Before we start',
          tap: "Tap 'Continue'",
          render: () => <Flow initial='ai-consent' option={2} />,
        },
        {
          id: 'chat-v2',
          title: 'Are you supporting someone?',
          tap: "'I'm happy with that'",
          render: () => <Flow initial='chat-v2-chosen' option={2} />,
        },
        {
          id: 'chat-v2-carer-name',
          title: 'Confirm your name',
          tap: "Tap 'Yes, I'm helping look after someone'",
          render: () => <Flow initial='chat-v2-carer-name' option={2} />,
        },
        {
          id: 'chat-v2-preferred-name',
          title: "Anything you'd prefer to be called",
          tap: "Tap 'Yes, that's me'",
          render: () => <Flow initial='chat-v2-preferred-name' option={2} />,
        },
        {
          id: 'chat-v2-mobile',
          title: 'Your mobile number',
          tap: "Tap 'No, Vez is fine'",
          render: () => <Flow initial='chat-v2-mobile' option={2} />,
        },
        {
          id: 'chat-v2-who',
          title: 'Their full name',
          tap: 'Type a mobile number',
          render: () => <Flow initial='chat-v2-who' option={2} />,
          alt: {
            title: 'Or their own name',
            render: () => <Flow initial='chat-v2-who-self' option={2} />,
          },
        },
        {
          id: 'chat-v2-situation',
          title: 'Tell us about it',
          tap: 'Type their name',
          render: () => <Flow initial='chat-v2-situation' option={2} />,
          alt: {
            title: 'Or a photo ready to send',
            render: () => <Flow initial='chat-v2-situation-photo' option={2} />,
          },
          note: (
            <Note title='Readme' variant='body2'>
              A photo waits in the composer before it sends, so they can check
              it is the right one. Send puts it in the thread; the cross takes
              it away. Files send straight away, as there is nothing to preview.
            </Note>
          ),
        },
        {
          id: 'chat-v2-recording',
          title: 'Recording',
          tap: 'Tap the orb',
          render: () => <Flow initial='chat-v2-recording' option={2} />,
        },
        {
          id: 'chat-v2-voice',
          title: 'Heard back before it sends',
          tap: 'Press it again',
          render: () => <Flow initial='chat-v2-voice' option={2} />,
        },
        {
          id: 'chat-v2-anything-more',
          title: 'Is that it?',
          tap: 'Send',
          render: () => <Flow initial='chat-v2-anything-more' option={2} />,
        },
        {
          // They kept going. Everything sent lands above "I'm done", so it is
          // still the last thing in the thread.
          id: 'chat-v2-anything-more-added',
          title: 'After sending more',
          tap: 'Send a voice note, a photo, then another',
          render: () => (
            <Flow initial='chat-v2-anything-more-added' option={2} />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              "I'm done" should persist at the bottom. Anything they send or
              upload lands above it, so it is always the last thing in the
              thread.
            </Note>
          ),
        },
        {
          id: 'processing',
          title: 'Processing everything you told us',
          tap: "Tap 'I'm done'",
          render: () => (
            <ProcessingChatScreen account={{ onSignOut: noop }} loop />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              Everything they sent is read here, at the end, rather than note by
              note as it arrived. Said in the chat rather than on a screen of
              its own: ending the conversation to say the conversation is over
              leaves nowhere to ask the one question whose answer does not
              depend on what comes back.
              <br />
              <br />A new chat, not the transcript. No orb and no composer:
              nothing is listening, and an input here would take an answer
              nothing receives. The alert floats where the composer would be so
              the status stays put as the messages arrive.
            </Note>
          ),
        },
        {
          id: 'done',
          title: 'The reading finishes',
          tap: 'Wait for the reading',
          render: () => (
            <ProcessingChatScreen
              account={{ onSignOut: noop }}
              from='invite'
              notice
            />
          ),
          note: (
            <Note title='Readme' variant='body2'>
              The answer comes back while they are still in the chat, so it
              arrives over the conversation rather than replacing it. A screen
              change here would take the conversation away in order to announce
              something about it.
              <br />
              <br />A notice with the way on in it, not a message from Keni.
              Keni asks and is answered; this is the system saying a job
              finished, and dressing that as a bubble puts an action in the
              transcript with no way to answer it.
            </Note>
          ),
        },
        {
          // A marker, not a screen: it says where the journey carries on, so the
          // row does not look like it stops here.
          id: 'to-review',
          title: 'Go to review flow',
          tap: "Tap 'Continue to review'",
          render: () => (
            <PlaceholderScreen
              title='Go to review flow'
              note='What we filled in, and the claim, are in the next row.'
            />
          ),
        },
      ],
    },
    // The capture as it stood before it was cut to one message. Shut by default:
    // it is here to be found when somebody asks what the follow-up looked like,
    // not to be read as part of the flow.
    {
      // Off the invite question while the reading happens. Its own row so the
      // main row runs straight from the processing chat to the reading
      // finishing.
      title: 'Who else helps',
      journey: 'Intake',
      steps: [
        {
          id: 'processing-alone',
          title: 'Nobody else helps',
          tap: "Tap 'No, it’s just me'",
          render: () => (
            <ProcessingChatScreen account={{ onSignOut: noop }} from='alone' />
          ),
        },
        {
          id: 'processing-explain',
          title: "What's a care circle?",
          tap: "Tap 'Yes, someone helps', then 'What's a care circle?'",
          render: () => (
            <ProcessingChatScreen
              account={{ onSignOut: noop }}
              from='explain'
            />
          ),
        },
        {
          id: 'processing-invite',
          title: 'Yes, invite them',
          tap: "Tap 'Yes, someone helps', then 'Invite to care circle'",
          render: () => (
            <ProcessingChatScreen account={{ onSignOut: noop }} from='email' />
          ),
        },
      ],
    },
    {
      title: 'Previous version: the follow-up question',
      collapsed: true,
      steps: [
        {
          id: 'chat-v2-more',
          title: 'Send as many as you want',
          tap: 'Send',
          render: () => <Flow initial='chat-v2-more' option={2} />,
        },
        {
          id: 'chat-v2-stuck',
          title: 'Five notes later, the way on is still there',
          tap: 'Keep sending',
          render: () => <Flow initial='chat-v2-stuck' option={2} />,
        },
      ],
    },
    {
      title: 'Review',
      journey: 'Review',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              One run rather than two. The ping arrives, they read back what we
              heard, and that takes them to what it filled in. No way around the
              app in the middle of it: nothing has been processed yet, so a tab
              bar has nowhere useful to go.
            </span>
            <span>
              Saying it reads right does not fill the gaps. Anything missing,
              disagreed or out of date still needs something nobody has done
              yet, which is what the actions list is for.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'ping',
          title: 'The ping arrives',
          render: () => (
            <LockScreen>
              <PushNotification {...INTAKE_PING} />
            </LockScreen>
          ),
        },
        {
          id: 'summary',
          title: 'Does this look right?',
          tap: 'Open it',
          render: () => <Flow initial='summary' option={2} />,
          alt: {
            title: 'Or editing it, after the pencil',
            render: () => <Flow initial='summary-editing' option={2} />,
          },
          note: (
            <Note title='Readme' variant='body2'>
              An overview of what they told us. We are not asking them to review
              every single thing here, so this is just the overview.
            </Note>
          ),
        },
        {
          id: 'review-facts',
          title: 'About Margaret',
          tap: 'Open it',
          render: () => <Flow initial='review-facts' option={2} />,
          alt: {
            title: 'Or framed as what we kept',
            render: () => <Flow initial='review-facts-kept' option={2} />,
          },
          note: (
            <Note title='Readme' variant='body2'>
              Then we ask them to review the things that live in the profile.
              These are the pieces of information that are used across more than
              one form. At this point they can answer anything that has not been
              answered yet, or leave it.
            </Note>
          ),
        },
        {
          id: 'home',
          title: 'Into the app',
          tap: 'Looks good to me',
          render: () => <InAppHome />,
          note: (
            <Note title='Readme' variant='body2'>
              Straight into the app after the review, rather than asking what
              they would like to do next. They land on Home, and the benefits
              calculator is a card on it.
            </Note>
          ),
        },
      ],
    },
    {
      title: 'When it is not quite right',
      journey: 'Review',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              The other answer to “Does this look right?”. It goes back into the
              conversation rather than into the list, because what is wrong with
              a summary is usually a sentence rather than a field: they said
              something and we heard it differently, and the way to fix that is
              to say it again.
            </span>
            <span>
              The chat reads back what we hold before it asks anything. It used
              to open on “anything else”, which is the right question at the end
              of an intake and the wrong one here — somebody who has just said
              we got it wrong has the correction in mind already, and an open
              invitation makes them find it on their own.
            </span>
            <span>
              It ends where the intake ended, so what they said goes through the
              same reading and the summary comes round again. The summary is the
              top of a loop, not a screen you pass once.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'summary-wrong',
          title: 'Does this look right?',
          tap: 'Open it',
          render: () => <Flow initial='summary' option={2} />,
          note: (
            <Note title='Two answers' variant='body2'>
              Both answers are on the screen: the one that carries on, and the
              one that says we misheard. Neither is hidden behind the other.
            </Note>
          ),
        },
        {
          id: 'again-readback',
          title: 'What we have got',
          tap: "'That's not quite right'",
          render: () => <Flow initial='chat-v2-again' option={2} />,
          note: (
            <Note title='Readme' variant='body2'>
              The conversation opens again, and Keni says back the same three
              lines the summary just showed. Same source, so the two cannot
              drift apart.
            </Note>
          ),
        },
        {
          id: 'again-whats-wrong',
          title: 'What have we got wrong?',
          tap: 'Read it back',
          render: () => <Flow initial='chat-v2-again' option={2} />,
          note: (
            <Note title='Readme' variant='body2'>
              Then the question, in its own bubble under the read-back. It stays
              open for as many corrections as they have: putting one thing right
              rarely turns out to be one thing.
            </Note>
          ),
        },
        {
          id: 'again-answered',
          title: 'Saying what changed',
          tap: 'Talk or type',
          render: () => <Flow initial='chat-v2-again-answered' option={2} />,
          note: (
            <Note title='Readme' variant='body2'>
              Answered the way everything else in the intake is answered, and
              the way on stays underneath however much they add.
            </Note>
          ),
        },
        {
          id: 'again-processing',
          title: 'Read again',
          tap: "Continue when you're done",
          render: () => <Flow initial='processing' option={2} />,
          note: (
            <Note title='Round again' variant='body2'>
              The same reading as the first time, and it pings them again when
              it is done. Back to the top of this row.
            </Note>
          ),
        },
      ],
    },
    {
      title: 'When we cannot read it',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Reading everything happens after they have gone, so a failure to
              read it cannot appear in the conversation: they are not in it. It
              has to come and find them, which makes it a flow rather than a
              state.
            </span>
            <span>
              It lands on the review screen rather than back in the chat. The
              question is what we did get, and that is the list this screen is
              already for. Telling us again is the way back to the conversation,
              offered rather than imposed.
            </span>
            <span>
              It opens the conversation back up on the note it is about, so what
              failed is in front of them rather than described to them. The way
              out is the composer, or a person.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'problem-ping',
          title: 'A different ping',
          render: () => (
            <LockScreen>
              <PushNotification {...INTAKE_PING_PROBLEM} />
            </LockScreen>
          ),
        },
        {
          id: 'summary-problem',
          title: 'What we did get',
          tap: 'Open it',
          render: () => <Flow initial='summary-problem' option={2} />,
          note: (
            <Note title='Why not the chat' variant='body2'>
              It used to reopen the conversation with the error in it, which put
              them back in a screen they had finished and left them to find the
              damage themselves. The same summary the happy path opens, with the
              bad news on top of it and the choice to carry on with what we have
              or say it again.
            </Note>
          ),
        },
        {
          id: 'tell-us-again',
          title: 'Saying it again',
          tap: "'Tell us again'",
          render: () => <Flow initial='chat-v2-again' option={2} />,
        },
        {
          id: 'processing-again',
          title: 'Read again',
          tap: "Continue when you're done",
          render: () => <Flow initial='processing' option={2} />,
          note: (
            <Note title='Round again' variant='body2'>
              Anything said a second time goes through the same reading as the
              first time, so it ends here and pings them again. The summary is
              the top of the loop, not a screen you pass once.
            </Note>
          ),
        },
      ],
    },
    // Both ways into the review from inside the app, held back rather than
    // dropped. They are the same review; what they add is a tab bar, an app
    // bar and four segments, at a point in the journey where nothing has been
    // processed yet and there is nowhere useful for those to go. Whether the
    // app is even open to them before the intake is read is the question, and
    // it is a bigger one than the review.
    {
      title: 'Getting there from the app',
      journey: 'Parked',
      note: (
        <Note title='Readme' variant='body2'>
          <Stack spacing={1.5}>
            <span>
              Not in this version. The run above is one way through: the ping
              arrives and the review happens, with no way around the app in the
              middle of it.
            </span>
            <span>
              These are here because the second entry point is a real need. Some
              people will not act on the ping, and the review has to wait
              somewhere for them. It waits here until we have decided what the
              app is allowed to show before the intake has been read.
            </span>
          </Stack>
        </Note>
      ),
      steps: [
        {
          id: 'intake-actions',
          title: 'Waiting on the actions list',
          render: () => <Flow initial='intake-actions' option={2} />,
        },
        {
          id: 'care-profile-unreviewed',
          title: 'The same facts on the profile, unreviewed',
          tap: 'Open the profile',
          render: () => <Flow initial='care-profile-unreviewed' option={2} />,
        },
      ],
    },
  ],
  states: [
    // What an upload looks like once it is in the chat. Happy rather than edge:
    // handing a photo or a file over is a way of answering the question, not a
    // deviation from it.
    {
      id: 'chat-v2-who-photo',
      row: 'Handing something over',
      title: 'A photo of their ID instead of a typed name',
      kind: 'happy',
      repro: 'Add a photo with the + on Their full name',
      render: () => <Flow initial='chat-v2-who-photo' option={2} />,
    },
    {
      id: 'chat-v2-who-photo-uploading',
      row: 'Handing something over',
      title: 'That photo still going',
      kind: 'happy',
      repro: 'While the photo is leaving the phone',
      render: () => <Flow initial='chat-v2-who-photo-uploading' option={2} />,
    },
    {
      id: 'chat-v2-more-doc-uploading',
      row: 'Handing something over',
      title: 'That document still going',
      kind: 'happy',
      repro: 'While the file is leaving the phone',
      render: () => <Flow initial='chat-v2-more-doc-uploading' option={2} />,
    },
    {
      id: 'chat-v2-more-doc',
      row: 'Handing something over',
      title: 'A document handed over mid conversation',
      kind: 'happy',
      repro: "Add a document with the plus on 'Is that it?'",
      render: () => <Flow initial='chat-v2-more-doc' option={2} />,
    },
    // Arriving on the profile unreviewed, and the profile itself. Two minis
    // because a mini cannot be clicked: with the sheet opening itself there was
    // no way to see the facts under it.
    {
      id: 'care-profile-arriving',
      title: 'Arriving on it, sheet up',
      kind: 'happy',
      row: 'The profile',
      journey: 'Parked',
      repro: 'Open the profile before anybody has said the facts read right',
      render: () => <Flow initial='care-profile-arriving' option={2} />,
    },
    // What a correction looks like, one per kind of answer. Editing happens on
    // the profile because that is where the reviewing happens.
    {
      id: 'review-facts-read',
      journey: 'Review',
      title: 'Changing something we read',
      kind: 'happy',
      row: 'Editing an answer',
      render: () => <Flow initial='review-facts-read' option={2} />,
    },
    {
      id: 'review-facts-gap',
      journey: 'Review',
      title: 'Filling something nobody answered',
      kind: 'happy',
      row: 'Editing an answer',
      render: () => <Flow initial='review-facts-gap' option={2} />,
    },
    {
      id: 'review-facts-conflict',
      journey: 'Review',
      title: 'Two sources disagree',
      kind: 'happy',
      row: 'Editing an answer',
      render: () => <Flow initial='review-facts-conflict' option={2} />,
    },
    {
      id: 'review-facts-stale',
      journey: 'Review',
      title: 'A fact that has gone stale',
      kind: 'happy',
      row: 'Editing an answer',
      render: () => <Flow initial='review-facts-stale' option={2} />,
    },
    // Not a b row: the other alts in this flow are the same question answered
    // the other way and carry on, where saying no leaves the flow. It is an edge
    // case until there is somewhere for it to go.
    {
      id: 'ai-consent-declined',
      title: AI_CONSENT_DECLINED.title,
      kind: 'edge',
      repro: "Tap 'No thanks' on Before we start",
      render: () => <Flow initial='ai-consent-declined' option={2} />,
    },
    // Two rows, because these are two different things. `Did not send` is the
    // transfer: nothing arrived, the thing they sent carries the failure, and the
    // way out is to send it again. `Error messages` is what we say once something
    // did arrive and we could not use it.
    //
    // All of them stay in the conversation either way: the flow's argument is
    // that things can arrive in any order and be read afterwards, so a full page
    // error would take away the thing they were part way through.
    {
      id: 'fail-upload-photo',
      row: 'Did not send',
      title: 'That photo would not upload',
      kind: 'error',
      repro: 'Lose the connection while the photo is going',
      render: () => <Flow initial='fail-upload-photo' option={2} />,
    },
    {
      id: 'fail-upload-doc',
      row: 'Did not send',
      title: 'That document would not upload',
      kind: 'error',
      repro: 'Lose the connection while the file is going',
      render: () => <Flow initial='fail-upload-doc' option={2} />,
    },
    {
      id: 'fail-mobile',
      row: 'Error messages',
      title: 'A mobile number that is too short',
      kind: 'error',
      repro:
        'Send a number under 11 digits to Your mobile number. Too long, or not starting 07, says so instead',
      render: () => <Flow initial='fail-mobile' option={2} />,
    },
    {
      id: 'fail-one-name',
      row: 'Error messages',
      title: 'Only a first name',
      kind: 'error',
      repro: 'Send one word to Their full name',
      render: () => <Flow initial='fail-one-name' option={2} />,
    },
    {
      id: 'fail-send',
      row: 'Did not send',
      title: 'A message would not send',
      kind: 'error',
      repro: 'Lose the connection with a note typed and sent',
      render: () => <Flow initial='fail-send' option={2} />,
    },
    {
      id: 'fail-send-voice',
      row: 'Did not send',
      title: 'A voice note would not send',
      kind: 'error',
      repro: 'Lose the connection with a recording sent',
      render: () => <Flow initial='fail-send-voice' option={2} />,
    },
  ],
  components: {},
};
